import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/contexts/PermissionsContext";
import {
  createAdminGroup,
  deleteAdminGroup,
  formatAdminApiError,
  listAdminGroups,
  updateAdminGroup,
} from "@/lib/adminGroupsApi";
import {
  formatScopeTooltip,
  getCatalogScopeNames,
  getScopeCatalogEntry,
  getScopeCatalogGroupedByArea,
} from "@/lib/scopeCatalog";
import { ALL_SCOPE_NAMES, HALO_SCOPE_NAMES } from "@/lib/scopeAuth";
import { impliedReadScopes } from "@/lib/scopeHierarchy";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info, Loader2, Plus, RefreshCw, Shield, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { permissionDeniedTitle } from "@/lib/permissionUi";

function ScopeInfo({ name }) {
  const entry = getScopeCatalogEntry(name);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label={entry ? `${entry.label} info` : `${name} info`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        sideOffset={6}
        collisionPadding={12}
        className="z-[60] w-72 max-w-[min(18rem,calc(100vw-2rem))] border bg-popover p-3 text-left text-popover-foreground shadow-md"
      >
        {entry ? (
          <div className="space-y-2 font-normal">
            <p className="text-sm font-semibold leading-snug text-foreground">{entry.label}</p>
            <p className="text-xs leading-relaxed text-foreground/80">{entry.description}</p>
            <p className="rounded-md bg-muted px-2 py-1.5 font-mono text-xs leading-snug text-foreground">
              {entry.name}
            </p>
            {entry.affects?.length > 0 && (
              <p className="text-xs leading-snug text-foreground/75 pt-1 border-t border-border/60">
                <span className="font-semibold text-foreground">Affects · </span>
                {entry.affects.join(" · ")}
              </p>
            )}
          </div>
        ) : (
          <p className="text-xs font-normal">{formatScopeTooltip(name)}</p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

function ScopeChecklist({ selected, onChange, disabled }) {
  const groups = useMemo(() => getScopeCatalogGroupedByArea(), []);
  const allNames = useMemo(() => getCatalogScopeNames(), []);
  const impliedReads = useMemo(
    () => impliedReadScopes(selected, ALL_SCOPE_NAMES),
    [selected],
  );
  const toggle = (name, checked) => {
    if (checked) onChange([...new Set([...selected, name])].sort());
    else onChange(selected.filter((s) => s !== name));
  };
  const allSelected = allNames.every((n) => selected.includes(n));
  const selectAll = () => onChange([...allNames].sort());
  const clearAll = () => onChange([]);
  return (
    <TooltipProvider delayDuration={0} skipDelayDuration={300}>
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || allSelected}
            onClick={selectAll}
          >
            Select all
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled || selected.length === 0}
            onClick={clearAll}
          >
            Clear
          </Button>
          <span className="self-center text-xs text-muted-foreground">
            {selected.length} selected
          </span>
        </div>
        <div className="max-h-80 overflow-y-auto rounded-md border bg-muted/20">
        {groups.map(({ area, scopes }, groupIndex) => (
          <div
            key={area}
            className={groupIndex > 0 ? "border-t border-border/70" : undefined}
          >
            <div className="sticky top-0 z-10 border-b border-border/60 bg-muted/95 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur-sm">
              {area}
            </div>
            <ul className="divide-y divide-border/50 px-1 py-1">
              {scopes.map((s) => {
                const stored = selected.includes(s.name);
                const implied = !stored && impliedReads.has(s.name);
                const checked = stored || implied;
                const rowDisabled = disabled || implied;
                return (
                  <li key={s.name}>
                    <div
                      className={[
                        "flex items-center gap-2 rounded-md px-2 py-2 transition-colors",
                        checked ? "bg-background" : "hover:bg-background/70",
                        rowDisabled ? "opacity-60" : "",
                      ].join(" ")}
                    >
                      <Checkbox
                        id={`scope-${s.name}`}
                        checked={checked}
                        disabled={rowDisabled}
                        onCheckedChange={(v) => toggle(s.name, Boolean(v))}
                      />
                      <label
                        htmlFor={`scope-${s.name}`}
                        className={[
                          "select-none text-sm font-medium leading-snug",
                          implied ? "cursor-default text-muted-foreground" : "cursor-pointer",
                        ].join(" ")}
                      >
                        {s.label}
                        {implied ? (
                          <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">
                            (implied by create/update/delete)
                          </span>
                        ) : null}
                      </label>
                      <ScopeInfo name={s.name} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        </div>
      </div>
    </TooltipProvider>
  );
}

const emptyForm = () => ({
  slug: "",
  display_name: "",
  description: "",
  scopes: ["dashboard.read", "session.read"],
});

/**
 * Settings → Group / Role Management — create roles and attach OAuth scopes.
 */
export default function SettingsGroupManagement() {
  const { idpMode } = useAuth();
  const entraMode = idpMode === "entra";
  const { hasPermission } = usePermissions();
  // Exact actions only — settings.group-management.write ORs create|update|delete
  // and would unlock edit/delete for create-only packs.
  const canCreate = hasPermission("admin.groups", "create")
    || hasPermission("settings.group-management", "create");
  const canUpdate = hasPermission("admin.groups", "update")
    || hasPermission("settings.group-management", "update");
  const canDelete = hasPermission("admin.groups", "delete")
    || hasPermission("settings.group-management", "delete");

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({ slug: "", displayName: "", scopes: "" });
  const [saving, setSaving] = useState(false);

  const clearFormErrors = () => setFormErrors({ slug: "", displayName: "", scopes: "" });

  const fieldErrorFromApi = (err) => {
    const msg = formatAdminApiError(err) || "";
    const status = err?.response?.status;
    const lowered = msg.toLowerCase();
    if (status === 409 || /duplicate/.test(lowered)) {
      if (/display.?name/.test(lowered)) {
        return {
          displayName: "This display name already exists. Please choose a different name.",
        };
      }
      return {
        slug: "This slug already exists. Please choose a different name.",
      };
    }
    return null;
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listAdminGroups(debouncedSearch || undefined);
      setRoles(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(formatAdminApiError(err));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    if (!canCreate) return;
    setForm(emptyForm());
    clearFormErrors();
    setCreateOpen(true);
  };

  const openEdit = (role) => {
    if (!canUpdate) return;
    clearFormErrors();
    setEditRole(role);
    setForm({
      slug: role.slug,
      display_name: role.display_name || "",
      description: role.description || "",
      // Drop pack orphans not in the product catalog (avoids 422 on save).
      scopes: [...(role.scopes || [])].filter((s) => HALO_SCOPE_NAMES.has(s)),
    });
  };

  const onCreate = async () => {
    if (!canCreate) return;
    clearFormErrors();
    if (!form.slug.trim() || !form.display_name.trim()) {
      toast.error("Slug and display name are required");
      return;
    }
    if (!form.scopes?.length) {
      setFormErrors((e) => ({
        ...e,
        scopes: "Select at least one scope for this role.",
      }));
      return;
    }
    setSaving(true);
    try {
      await createAdminGroup({
        slug: form.slug.trim().toLowerCase(),
        display_name: form.display_name.trim(),
        description: form.description.trim() || null,
        scopes: form.scopes,
      });
      toast.success("Role created");
      setCreateOpen(false);
      clearFormErrors();
      await load();
    } catch (err) {
      const fieldErr = fieldErrorFromApi(err);
      if (fieldErr) {
        setFormErrors((e) => ({ ...e, ...fieldErr }));
      } else {
        toast.error(formatAdminApiError(err));
      }
    } finally {
      setSaving(false);
    }
  };

  const onSaveEdit = async () => {
    if (!canUpdate || !editRole) return;
    clearFormErrors();
    if (!form.scopes?.length) {
      setFormErrors((e) => ({
        ...e,
        scopes: "Select at least one scope for this role.",
      }));
      return;
    }
    setSaving(true);
    try {
      await updateAdminGroup(editRole.slug, {
        display_name: form.display_name.trim(),
        description: form.description.trim() || null,
        scopes: form.scopes,
      });
      toast.success("Role updated");
      setEditRole(null);
      clearFormErrors();
      await load();
    } catch (err) {
      const fieldErr = fieldErrorFromApi(err);
      if (fieldErr) {
        setFormErrors((e) => ({ ...e, ...fieldErr }));
      } else {
        toast.error(formatAdminApiError(err));
      }
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (role) => {
    if (!canDelete) return;
    if (role.slug === "administrator" || role.is_system) {
      toast.error("Cannot delete a system role");
      return;
    }
    if (!window.confirm(`Delete role “${role.display_name || role.slug}”? Users in this role will lose access until reassigned.`)) {
      return;
    }
    try {
      await deleteAdminGroup(role.slug);
      toast.success("Role deleted");
      await load();
    } catch (err) {
      toast.error(formatAdminApiError(err));
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Group / Role management
          </CardTitle>
          <CardDescription>
            Create roles, attach OAuth scopes, and manage which areas of Halo each role can access.
            {entraMode
              ? " Under Microsoft Entra, custom roles are created as app roles on the Halo API app registration."
              : null}
            Hover the info icon next to a scope to see what it unlocks.
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Dialog
            open={createOpen}
            onOpenChange={(open) => {
              if (open && !canCreate) return;
              setCreateOpen(open);
              if (!open) clearFormErrors();
            }}
          >
            <span title={permissionDeniedTitle(canCreate, "create roles")}>
              <DialogTrigger asChild>
                <Button size="sm" onClick={openCreate} disabled={!canCreate}>
                  <Plus className="h-4 w-4 mr-1" />
                  Create role
                </Button>
              </DialogTrigger>
            </span>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create role</DialogTitle>
                <DialogDescription>
                  Slug becomes the Cognito group name and cannot be renamed later.
                </DialogDescription>
              </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="role-slug">Slug</Label>
                    <Input
                      id="role-slug"
                      value={form.slug}
                      onChange={(e) => {
                        setForm((f) => ({ ...f, slug: e.target.value }));
                        if (formErrors.slug) setFormErrors((er) => ({ ...er, slug: "" }));
                      }}
                      placeholder="e.g. ticket-lead"
                      aria-invalid={Boolean(formErrors.slug)}
                      className={formErrors.slug ? "border-destructive" : undefined}
                    />
                    {formErrors.slug ? (
                      <p className="mt-1 text-sm text-destructive">{formErrors.slug}</p>
                    ) : null}
                  </div>
                  <div>
                    <Label htmlFor="role-name">Display name</Label>
                    <Input
                      id="role-name"
                      value={form.display_name}
                      onChange={(e) => {
                        setForm((f) => ({ ...f, display_name: e.target.value }));
                        if (formErrors.displayName) {
                          setFormErrors((er) => ({ ...er, displayName: "" }));
                        }
                      }}
                      aria-invalid={Boolean(formErrors.displayName)}
                      className={formErrors.displayName ? "border-destructive" : undefined}
                    />
                    {formErrors.displayName ? (
                      <p className="mt-1 text-sm text-destructive">{formErrors.displayName}</p>
                    ) : null}
                  </div>
                  <div>
                    <Label htmlFor="role-desc">Description</Label>
                    <Textarea
                      id="role-desc"
                      value={form.description}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2 pt-1">
                    <div>
                      <Label>Scopes</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Select the permissions this role should grant. Hover ⓘ for what each scope unlocks.
                      </p>
                    </div>
                    <ScopeChecklist
                      selected={form.scopes}
                      onChange={(scopes) => {
                        setForm((f) => ({ ...f, scopes }));
                        if (formErrors.scopes) setFormErrors((er) => ({ ...er, scopes: "" }));
                      }}
                      disabled={saving}
                    />
                    {formErrors.scopes ? (
                      <p className="text-sm text-destructive">{formErrors.scopes}</p>
                    ) : null}
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                  <Button onClick={onCreate} disabled={saving || !canCreate}>
                    {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                    Create
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            placeholder="Search by name or slug…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm"
          />
        </div>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-8">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading roles…
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Scopes</TableHead>
                <TableHead className="w-[140px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map((role) => (
                <TableRow key={role.slug}>
                  <TableCell>
                    <div className="font-medium">{role.display_name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{role.slug}</div>
                    {role.is_system && <Badge variant="secondary" className="mt-1">System</Badge>}
                  </TableCell>
                  <TableCell className="text-sm max-w-xs">{role.description || "—"}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{(role.scopes || []).length} scopes</Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <span title={permissionDeniedTitle(canUpdate, "edit roles")}>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={!canUpdate}
                        onClick={() => openEdit(role)}
                      >
                        Edit
                      </Button>
                    </span>
                    {!role.is_system && role.slug !== "administrator" ? (
                      <span title={permissionDeniedTitle(canDelete, "delete roles")}>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!canDelete}
                          onClick={() => onDelete(role)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </span>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
              {!roles.length && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    No roles found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}

        <Dialog
          open={Boolean(editRole)}
          onOpenChange={(o) => {
            if (!o) {
              setEditRole(null);
              clearFormErrors();
            }
          }}
        >
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit role</DialogTitle>
              <DialogDescription>
                Slug <span className="font-mono">{editRole?.slug}</span> cannot be renamed.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Display name</Label>
                <Input
                  value={form.display_name}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, display_name: e.target.value }));
                    if (formErrors.displayName) {
                      setFormErrors((er) => ({ ...er, displayName: "" }));
                    }
                  }}
                  aria-invalid={Boolean(formErrors.displayName)}
                  className={formErrors.displayName ? "border-destructive" : undefined}
                />
                {formErrors.displayName ? (
                  <p className="mt-1 text-sm text-destructive">{formErrors.displayName}</p>
                ) : null}
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={2}
                />
              </div>
              <div className="space-y-2 pt-1">
                <div>
                  <Label>Scopes</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Select the permissions this role should grant. Hover ⓘ for what each scope unlocks.
                  </p>
                </div>
                <ScopeChecklist
                  selected={form.scopes}
                  onChange={(scopes) => {
                    setForm((f) => ({ ...f, scopes }));
                    if (formErrors.scopes) setFormErrors((er) => ({ ...er, scopes: "" }));
                  }}
                  disabled={saving || !!editRole?.is_system}
                />
                {formErrors.scopes ? (
                  <p className="text-sm text-destructive">{formErrors.scopes}</p>
                ) : null}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditRole(null)}>Cancel</Button>
              <Button onClick={onSaveEdit} disabled={saving || !canUpdate}>
                {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
